import * as React from "react"
import { HelpCircle, Info } from "lucide-react"
import { useLingui } from "@lingui/react"
import { Link } from "react-router"

import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export interface HelpTipProps {
  /** The explanation. Keep it to one or two sentences; a `\n` starts a new line. */
  content: React.ReactNode
  /** Accessible name of the icon button; defaults to "Help". */
  label?: string
  side?: React.ComponentProps<typeof TooltipContent>["side"]
  align?: React.ComponentProps<typeof TooltipContent>["align"]
  className?: string
  /** A platform guide topic id (`team.shifts`): adds a "Learn more" link to it. */
  guideTopic?: string
  /** `info` shows an "i" instead of the question mark, e.g. for how a figure is calculated. */
  icon?: "help" | "info"
  "data-testid"?: string
}

/**
 * Whether a focus is one the user sees as keyboard focus. A dialog that moves its
 * initial focus onto the icon after a mouse click focuses it without
 * `:focus-visible`; the tooltip must not open then (#2119).
 */
function isKeyboardFocus(element: HTMLElement): boolean {
  try {
    return element.matches(":focus-visible")
  } catch {
    return true
  }
}

/**
 * A small question-mark icon that explains the control next to it. Opens on
 * hover, on click (so it works on touch screens) and on keyboard focus; a focus
 * the user did not make with the keyboard does not open it. Place it next to a
 * label, never inside a `<label>`.
 */
export function HelpTip({
  content,
  label,
  side = "top",
  align = "center",
  className,
  guideTopic,
  icon = "help",
  "data-testid": testId = "help-tip",
}: HelpTipProps) {
  const Icon = icon === "info" ? Info : HelpCircle
  const { i18n } = useLingui()
  const [open, setOpen] = React.useState(false)
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label ?? i18n._({ id: "common.help", message: "Help" })}
            data-testid={testId}
            onFocus={(event) => {
              // Radix opens on focus unless the event is prevented.
              if (!isKeyboardFocus(event.currentTarget)) event.preventDefault()
            }}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setOpen((value) => !value)
            }}
            className={cn(
              "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring print:hidden",
              className
            )}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className="max-w-xs whitespace-pre-line text-left font-normal"
        >
          {content}
          {guideTopic ? (
            <Link
              to={`/guide/${guideTopic}`}
              className="mt-1 block font-medium underline underline-offset-2"
              data-testid={`${testId}-guide-link`}
            >
              {i18n._({ id: "guide.learnMore", message: "Learn more" })}
            </Link>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
